import type { MessageKey } from './sq'

export const en: Record<MessageKey, string> = {
  'app.name': 'Fjalim',
  'app.tagline': 'Albanian transcription and captions',
  'nav.skip': 'Skip to content',
  'nav.main': 'Main navigation',
  'nav.transcriptions': 'Transcriptions',
  'nav.keys': 'API keys',
  'nav.billing': 'Billing',
  'nav.admin': 'Admin',
  'nav.logout': 'Log out',
  'nav.login': 'Log in',
  'nav.signup': 'Sign up',
  'theme.label': 'Theme',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'System',
  'lang.label': 'Language',
  'balance.label': 'Balance',
  'common.loading': 'Loading…',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.retry': 'Try again',
  'common.back': 'Back',
  'common.optional': 'optional',
  'common.never': 'Never',
  'common.minutes': '{n} min',
  'dur.h': 'h',
  'dur.min': 'min',
  'dur.s': 's',

  'err.generic': 'Something went wrong. Try again.',
  'err.network_error': 'Could not reach the server. Check your connection.',
  'err.invalid_credentials': 'The email or password is wrong.',
  'err.invalid_token': 'The link is invalid or has expired.',
  'err.insufficient_credits': 'Your balance is too low. Top up to continue.',
  'err.rate_limited': 'Too many requests. Wait a moment and try again.',
  'err.too_many_keys': 'You have reached the limit of active keys.',
  'err.billing_unavailable': 'Payments are not available right now.',
  'err.version_conflict': 'A newer version was saved elsewhere.',
  'err.not_found': 'Not found.',
  'err.unauthorized': 'Log in again to continue.',
  'err.upload_incomplete': 'The upload did not finish. Try again.',
  'err.size_mismatch': 'The upload did not finish. Try again.',
  'err.validation_error': 'Check the fields and try again.',
  'err.email_not_allowed': 'This email provider is not accepted. Use another address.',
  'err.turnstile_failed': 'The security check did not pass. Try again.',

  'auth.field.email': 'Email',
  'auth.field.password': 'Password',
  'auth.field.newPassword': 'New password',
  'auth.field.name': 'Name',
  'auth.field.passwordHint': 'At least 10 characters.',
  'auth.login.title': 'Log in to Fjalim',
  'auth.login.lead': 'Pick up where you left off.',
  'auth.login.submit': 'Log in',
  'auth.login.noAccount': 'No account yet?',
  'auth.login.terms': 'By logging in, you accept the Terms of service and Privacy policy.',
  'auth.signup.title': 'Create an account',
  'auth.signup.lead': 'Verify your email and get {minutes} free minutes of transcription.',
  'auth.signup.submit': 'Create account',
  'auth.signup.check.title': 'Check your email',
  'auth.signup.check.body': 'We sent you an email with the next steps. Open it to continue, or log in if you already have an account.',
  'auth.signup.haveAccount': 'Already have an account?',
  'auth.signup.terms': 'I accept the Terms of service and Privacy policy.',
  'auth.forgot.link': 'Forgot your password?',
  'auth.forgot.title': 'Reset your password',
  'auth.forgot.lead': 'Enter your email and we will send you a link.',
  'auth.forgot.submit': 'Send link',
  'auth.forgot.sent': 'If an account exists for this email, the link has been sent.',
  'auth.reset.title': 'New password',
  'auth.reset.submit': 'Save password',
  'auth.reset.done': 'Your password was changed. Log in with the new one.',
  'auth.reset.missing': 'This link is incomplete. Request a new one.',
  'auth.verify.title': 'Email verification',
  'auth.verify.working': 'Verifying your email…',
  'auth.verify.done': 'Your email is verified.',
  'auth.verify.grant': 'We added {minutes} free minutes of transcription.',
  'auth.verify.noGrant': 'Your account is verified.',
  'auth.verify.failed': 'The link is invalid or has expired.',
  'auth.verify.continue': 'Continue to transcriptions',
  'auth.verify.missing': 'This link is incomplete.',
  'auth.pending.title': 'Verify your email',
  'auth.pending.body': 'We sent a link to {email}. After you verify, you get {minutes} free minutes.',
  'auth.pending.resend': 'Send again',
  'auth.pending.sent': 'The link was sent again.',
  'auth.pending.already': 'Your email is already verified.',

  'list.title': 'Transcriptions',
  'list.upload.cta': 'Upload files',
  'list.drop.title': 'Drop files here',
  'list.drop.hint': 'or press to choose · audio and video, up to 2 GB per file',
  'list.drop.active': 'Release to upload',
  'list.filters': 'Search and filters',
  'list.search.label': 'Search files',
  'list.search.placeholder': 'Search by file name',
  'list.filter.all': 'All',
  'list.filter.completed': 'Ready',
  'list.filter.processing': 'Processing',
  'list.filter.queued': 'Queued',
  'list.filter.failed': 'Failed',
  'list.sort.label': 'Sort',
  'list.sort.newest': 'Newest',
  'list.sort.oldest': 'Oldest',
  'list.sort.duration': 'Duration',
  'list.results.one': '{n} file',
  'list.results.other': '{n} files',
  'list.clear': 'Clear',
  'list.emptyFiltered.title': 'No files match',
  'list.emptyFiltered.reset': 'Reset filters',
  'list.empty.title': 'No transcriptions yet',
  'list.empty.body': 'Upload an audio or video file. The transcript appears here within minutes.',
  'list.col.name': 'File',
  'list.col.status': 'Status',
  'list.col.duration': 'Duration',
  'list.col.cost': 'Cost',
  'list.col.created': 'Created',
  'list.status.queued': 'Queued',
  'list.status.processing': 'Processing',
  'list.status.completed': 'Ready',
  'list.status.failed': 'Failed',
  'list.status.canceled': 'Canceled',
  'list.upload.uploading': 'Uploading {pct}%',
  'list.upload.starting': 'Creating the transcription…',
  'list.upload.done': 'Uploaded',
  'list.upload.failed': 'Upload failed',
  'list.upload.tooBig': 'The file is over 2 GB.',
  'list.upload.dismiss': 'Remove from list',
  'list.upload.retry': 'Try again',
  'list.untitled': 'Untitled',
  'list.open': 'Open',
  'list.delete': 'Delete',
  'list.delete.title': 'Delete this transcription?',
  'list.delete.body': 'The audio and every transcript version are deleted. This cannot be undone.',
  'list.delete.confirm': 'Delete permanently',
  'list.more': 'Show more',
  'list.lowBalance': 'Your balance is low. Top up to transcribe.',
  'list.topup': 'Top up',
  'list.live': 'The list updates by itself.',
  'list.uploadsHeading': 'Uploads',
  'list.empty.step1': 'Upload an audio or video file.',
  'list.empty.step2': 'Wait a few minutes. The transcript appears here by itself.',
  'list.empty.step3': 'Fix words, speakers and times, then export TXT, SRT or VTT.',
  'list.speakers.toggle': 'Separate speakers (+{price} per minute)',
  'list.speakers.countLabel': 'Number of speakers',
  'list.speakers.auto': 'Automatic',
  'list.speakers.one': '{n} speaker',
  'list.speakers.other': '{n} speakers',

  'editor.back': 'Transcriptions',
  'editor.tab.transcript': 'Transcript',
  'editor.tab.captions': 'Captions',
  'editor.tabs': 'Editor views',
  'editor.loading': 'Opening the transcript…',
  'editor.notReady': 'This transcription is not ready ({status}).',
  'editor.failed': 'The transcription failed: {message}',
  'editor.noAudio': 'The audio is not available.',
  'editor.reload': 'Reload',
  'editor.segments': 'Transcript segments',
  'editor.empty': 'The transcript is empty.',
  'player.label': 'Player',
  'player.play': 'Play',
  'player.pause': 'Pause',
  'player.back5': 'Back 5 s',
  'player.fwd5': 'Forward 5 s',
  'player.speed': 'Speed',
  'player.seek': 'Position',
  'player.follow': 'Follow playback',
  'seg.time': 'Go to {time}',
  'seg.editing': 'Segment text',
  'seg.speaker': 'Speaker',
  'seg.speaker.none': 'No speaker',
  'seg.speaker.rename': 'Rename',
  'seg.speaker.renamed': 'All matching speakers were renamed.',
  'seg.speaker.new': '+ New speaker…',
  'seg.speaker.newName': 'New speaker name',
  'seg.speaker.add': 'Add',
  'seg.speaker.renameAll': 'Rename everywhere',
  'seg.speaker.renameTo': 'New name for this speaker',
  'seg.start': 'Start',
  'seg.end': 'End',
  'seg.split': 'Split at cursor',
  'seg.merge': 'Merge with next',
  'seg.menu': 'Segment actions',
  'seg.more': 'More actions',
  'seg.more.tip': 'More actions for this segment: split, merge, change speaker, set times, delete (right-click or Shift+F10)',
  'seg.split.tip': 'Split the segment at the cursor ({key})',
  'seg.merge.tip': 'Merge this segment with the one after it',
  'seg.mergePrev': 'Merge with previous',
  'seg.speaker.change': 'Change speaker…',
  'seg.setStart': 'Set start here',
  'seg.setEnd': 'Set end here',
  'seg.delete': 'Delete segment',
  'seg.delete.title': 'Delete this segment?',
  'seg.delete.body': 'The segment and its times are removed from the transcript.',
  'seg.delete.confirm': 'Delete',
  'shortcuts.next': 'Finish the segment and go to the next one',
  'shortcuts.split': 'Split the segment at the cursor',
  'shortcuts.seekWord': '+ click on a word: play from that word',
  'shortcuts.menu': 'Open the segment menu (or right-click)',
  'cap.split.tip': 'Split the cue at the cursor',
  'cap.merge.tip': 'Merge this cue with the one after it',
  'cap.delete.tip': 'Delete this cue',
  'cap.tools': 'Actions for cue {n}',
  'cap.col.text': 'Text',
  'cap.col.speaker': 'Speaker',
  'time.here': 'Set here',
  'time.here.label': 'Set {edge} to the current playback position',
  'time.notice.negative': 'Time cannot be negative. We corrected it.',
  'time.notice.reversed': 'The start must come before the end. We corrected the time.',
  'time.notice.overlapPrev': 'A segment cannot start before the previous one ends. We corrected the time.',
  'time.notice.overlapNext': 'A segment cannot end after the next one starts. We corrected the time.',
  'find.open': 'Find',
  'find.title': 'Find and replace',
  'find.query': 'Find',
  'find.replacement': 'Replace with',
  'find.ignoreCase': 'Ignore upper and lower case',
  'find.count': '{current} of {total}',
  'find.none': 'No results',
  'find.next': 'Next',
  'find.prev': 'Previous',
  'find.replaceOne': 'Replace',
  'find.replaceAll': 'Replace all',
  'find.replaced.one': 'Replaced {n} match.',
  'find.replaced.other': 'Replaced {n} matches.',
  'find.close': 'Close find',
  'find.hintDiacritics': 'ë and e, ç and c are different letters.',
  'save.saved': 'Saved · version {n}',
  'save.saving': 'Saving…',
  'save.unsaved': 'Unsaved changes',
  'save.error': 'Saving failed. We will retry with your next change.',
  'save.now': 'Save',
  'save.conflict': 'A newer version was saved elsewhere. Reload?',
  'save.conflictHint': 'Reloading discards your unsaved changes.',
  'guard.leave': 'You have unsaved changes. Leave anyway?',
  'guard.stay': 'Stay',
  'guard.discard': 'Leave without saving',
  'export.label': 'Export',
  'export.txt': 'TXT',
  'export.txt-timestamps': 'TXT with timestamps',
  'export.srt': 'SRT',
  'export.vtt': 'VTT',
  'export.json': 'JSON',
  'export.failed': 'The export failed. Try again.',
  'export.transcript': 'Transcript',
  'export.captions': 'Captions',
  'export.for': 'For',
  'export.go': 'Download',
  'export.busy': 'Preparing…',
  'export.hint.one': '{aspect}, {n} line, up to {chars} characters',
  'export.hint.other': '{aspect}, {n} lines, up to {chars} characters',
  'export.edited': 'Your edited captions are exported as they are; “For” only shapes automatically built captions.',

  'pv.title': 'Preview',
  'pv.platform': 'Platform',
  'pv.chip.youtube': 'YouTube',
  'pv.chip.youtube-shorts': 'Shorts',
  'pv.chip.tiktok': 'TikTok',
  'pv.chip.instagram-reels': 'Reels',
  'pv.chip.instagram': 'Instagram',
  'pv.chip.facebook': 'Facebook',
  'pv.chip.linkedin': 'LinkedIn',
  'pv.chip.x': 'X',
  'pv.chip.broadcast': 'News/TV',
  'pv.frame': 'Video preview with captions ({aspect})',
  'pv.safe': 'Show safe area',
  'pv.safeNote': 'Roughly where the platform places its buttons and description.',
  'pv.audioNote': 'The source is audio: captions are placed on the canvas colour.',
  'pv.styles': 'Quick styles',
  'pv.style.classic': 'Classic',
  'pv.style.box': 'Box',
  'pv.style.tiktok': 'TikTok',
  'pv.style.news': 'News',
  'pv.font': 'Font',
  'pv.font.grotesk': 'Grotesk',
  'pv.font.condensed': 'Condensed',
  'pv.font.serif': 'Serif',
  'pv.size': 'Size',
  'pv.color': 'Text colour',
  'pv.bg': 'Background',
  'pv.bg.none': 'None',
  'pv.bg.outline': 'Outline',
  'pv.bg.box': 'Box',
  'pv.bgColor': 'Background colour',
  'pv.bgOpacity': 'Opacity',
  'pv.position': 'Position',
  'pv.position.bottom': 'Bottom',
  'pv.position.middle': 'Middle',
  'pv.position.top': 'Top',
  'pv.uppercase': 'Uppercase',
  'pv.highlight': 'Highlight the spoken word',
  'pv.highlightColor': 'Highlight colour',
  'pv.canvas': 'Canvas colour',
  'pv.framing': 'Framing',
  'pv.framing.fit': 'Whole',
  'pv.framing.crop': 'Cropped',
  'pv.framingNote.fit': 'The whole video is shown; the background is a blurred copy.',
  'pv.framingNote.crop': 'The video fills the frame and the edges are cropped.',
  'render.title': 'Videos',
  'render.create': 'Create video with captions',
  'render.creating': 'Sending…',
  'render.price.free': 'Free',
  'render.price.freeLeft.one': 'Free — you have {n} free video left',
  'render.price.freeLeft.other': 'Free — you have {n} free videos left',
  'render.price.paid': 'Costs {amount} ({n} min)',
  'render.pricing': '{free} free videos per account, then {amount} per minute.',
  'render.cost.free': 'Free',
  'render.cost.refunded': 'Not charged',
  'render.topup': 'Top up your balance',
  'render.firstJob': 'The first video can take a few minutes.',
  'render.for': 'For {platform}',
  'render.empty': 'Videos with captions that you create appear here.',
  'render.status.queued': 'Queued',
  'render.status.processing': 'Creating',
  'render.status.completed': 'Ready',
  'render.status.failed': 'Failed',
  'render.status.canceled': 'Canceled',
  'render.download': 'Download',
  'render.expires': 'deleted on {date}',
  'render.retry': 'Try again',
  'render.remove': 'Remove',
  'render.failed': 'The video could not be created. Please try again.',
  'render.framing.fit': 'whole',
  'render.framing.crop': 'cropped',
  'err.render_limit': 'You already have two videos being created. Wait for one to finish, then try again.',
  'err.source_too_long': 'The source is longer than 30 minutes. Videos with captions support up to 30 minutes.',
  'err.no_source_media': 'The original file was not stored, so a video with captions cannot be created.',
  'shortcuts.title': 'Keyboard shortcuts',
  'shortcuts.playPause': 'Play / pause',
  'shortcuts.back5': 'Back 5 seconds',
  'shortcuts.fwd5': 'Forward 5 seconds',
  'shortcuts.save': 'Save',
  'shortcuts.find': 'Find and replace',
  'shortcuts.help': 'Show or hide this list',
  'shortcuts.hint': 'Press ? for shortcuts',
  'shortcuts.open': 'Shortcuts',

  'cap.title': 'Captions',
  'cap.preview': 'Caption preview',
  'cap.audioPanel': 'Caption showing now',
  'cap.derived': 'Captions are built from the transcript. Your first edit saves them as independent captions.',
  'cap.edited': 'Captions were edited and no longer follow transcript changes.',
  'cap.cue': 'Cue {n}',
  'cap.text': 'Text of cue {n}',
  'cap.start': 'Start',
  'cap.end': 'End',
  'cap.nudge': '{edge} {delta} s',
  'cap.split': 'Split at cursor',
  'cap.split.tooShort': 'The cue is too short to split.',
  'cap.merge': 'Merge with next',
  'cap.delete': 'Delete cue',
  'cap.seek': 'Go to cue',
  'cap.speaker': 'Speaker of caption {n}',
  'cap.warnings.one': '{n} warning',
  'cap.warnings.other': '{n} warnings',
  'cap.errors.one': '{n} error',
  'cap.errors.other': '{n} errors',
  'cap.allGood': 'All captions pass the rules.',
  'cap.regen': 'Rebuild from transcript',
  'cap.regen.title': 'Rebuild the captions?',
  'cap.regen.body': 'Your caption edits are replaced with new captions built from the transcript words, using the rules above.',
  'cap.regen.confirm': 'Rebuild',
  'cap.rules': 'Caption rules',
  'cap.rule.maxCharsPerLine': 'Characters per line',
  'cap.rule.maxLines': 'Lines per cue',
  'cap.rule.maxCps': 'Characters per second',
  'cap.rule.minDuration': 'Shortest duration (s)',
  'cap.rule.maxDuration': 'Longest duration (s)',
  'cap.rule.minGap': 'Smallest gap (s)',
  'cap.rule.reset': 'Reset to defaults',
  'cap.import': 'Import SRT or VTT',
  'cap.import.failed': 'The file could not be read as SRT or VTT.',
  'cap.import.empty': 'The file has no cues.',
  'cap.empty': 'There are no captions.',
  'cap.issue.line_too_long': 'The line is too long.',
  'cap.issue.too_many_lines': 'There are too many lines.',
  'cap.issue.cps_too_high': 'Reads too fast.',
  'cap.issue.too_short': 'Shown for too short a time.',
  'cap.issue.too_long': 'Shown for too long.',
  'cap.issue.overlap': 'Overlaps the next cue.',
  'cap.issue.gap_too_small': 'The gap to the next cue is too small.',
  'cap.issue.empty': 'The cue is empty.',
  'cap.issue.negative_duration': 'The end is before the start.',

  'keys.title': 'API keys',
  'keys.lead': 'Use keys to call the API from your own programs. Each key is shown only once.',
  'keys.create': 'Create key',
  'keys.name': 'Key name',
  'keys.name.placeholder': 'e.g. Production',
  'keys.created': 'Key created. Copy it now, you will not see it again.',
  'keys.list': 'Your keys',
  'keys.empty': 'No keys yet.',
  'keys.prefix': 'Prefix',
  'keys.createdAt': 'Created',
  'keys.lastUsed': 'Last used',
  'keys.revoke': 'Revoke key',
  'keys.revoke.title': 'Revoke this key?',
  'keys.revoke.body': 'Programs using it will get a 401 error immediately.',
  'keys.revoked': 'Revoked',
  'webhook.title': 'Webhook secret',
  'webhook.lead': 'Every webhook is signed with this secret in the Fjalim-Signature header.',
  'webhook.reveal': 'Reveal',
  'webhook.hide': 'Hide',
  'webhook.rotate': 'Rotate secret',
  'webhook.rotate.title': 'Rotate the secret?',
  'webhook.rotate.body': 'The old secret stops working immediately. Update your programs.',
  'webhook.rotated': 'The secret was rotated.',
  'curl.title': 'curl example',
  'curl.lead': 'Replace fj_live_… with your key.',

  'billing.title': 'Billing',
  'billing.balance': 'Current balance',
  'billing.expires': 'Expires on {date}',
  'billing.minutesLeft.one': 'about {minutes} minute of transcription',
  'billing.minutesLeft.other': 'about {minutes} minutes of transcription',
  'billing.topup': 'Top up',
  'billing.custom': 'Other amount',
  'billing.custom.hint': 'From €{min} to €{max}, in whole euros.',
  'billing.pay': 'Continue to payment',
  'billing.redirecting': 'Taking you to payment…',
  'billing.invalidAmount': 'Enter a whole amount from €{min} to €{max}.',
  'billing.success': 'Payment received. Your balance updates within a few seconds.',
  'billing.canceled': 'Payment canceled. You were not charged.',
  'billing.price.title': 'Price',
  'billing.price.line': 'From {price} per minute, billed for every started second.',
  'billing.price.list': 'Price list',
  'billing.price.fast': 'Fast',
  'billing.price.accurate': 'Accurate',
  'billing.price.speakers': 'Speaker labels',
  'billing.price.perMinute': '{price}/min',
  'billing.price.addon': '+{price}/min',
  'billing.price.active': 'Active',
  'billing.price.soon': 'Coming soon',
  'billing.price.planned': 'Planned prices may change.',
  'billing.compare': 'Google Cloud STT (Chirp 3): {google} per minute, and Albanian is still in Preview. Checked on {date}.',
  'billing.ledger': 'Ledger',
  'billing.ledger.empty': 'No movements yet.',
  'billing.ledger.more': 'Show more',
  'billing.usage': 'Daily usage',
  'billing.usage.empty': 'No usage in the last 30 days.',
  'ledger.topup': 'Top-up',
  'ledger.signup_grant': 'Free minutes',
  'ledger.usage': 'Transcription',
  'ledger.usage_addon': 'Speaker labels',
  'ledger.usage_refund': 'Transcription refund',
  'ledger.refund': 'Payment refund',
  'ledger.adjustment': 'Adjustment',
  'ledger.render': 'Caption render',
  'ledger.render_refund': 'Caption render refund',
  'ledger.expiry': 'Expired credit',
  'ledger.date': 'Date',
  'ledger.kind': 'Type',
  'ledger.amount': 'Amount',

  'admin.title': 'Admin',
  'admin.lead': 'No subscriptions: revenue is top-ups minus Stripe refunds over the last 30 days.',
  'admin.kpis': 'Key figures',
  'admin.kpi.revenue': 'Revenue 30d',
  'admin.kpi.refunds': 'Refunds 30d',
  'admin.kpi.runRate': 'Run rate / month',
  'admin.kpi.paying': 'Paying accounts',
  'admin.kpi.active': 'Active accounts',
  'admin.kpi.minutes': 'Minutes 30d',
  'admin.kpi.usage': 'Usage 30d',
  'admin.kpi.held': 'Balances held',
  'admin.kpi.owed': 'Balances owed',
  'admin.kpi.cost': 'Est. compute cost',
  'admin.daily': 'Daily',
  'admin.chart': 'Daily minutes and revenue',
  'admin.legend.minutes': 'Minutes',
  'admin.legend.revenue': 'Revenue',
  'admin.accounts': 'Accounts',
  'admin.search': 'Search email',
  'admin.sort.spend': 'Spend',
  'admin.sort.recent': 'Newest',
  'admin.sort.minutes': 'Minutes',
  'admin.col.email': 'Email',
  'admin.col.minutes': 'Minutes 30d',
  'admin.col.spend': 'Spend 30d',
  'admin.col.balance': 'Balance',
  'admin.unitMinutes': '{n} min',
  'admin.test': 'test',
  'admin.empty': 'No accounts.',
  'notfound.title': 'Page not found',
  'notfound.body': 'The link is old or wrong.',
  'notfound.back': 'Back to transcriptions',
  'crash.title': 'Something went wrong',
  'crash.body': 'The editor hit an unexpected error. Your saved changes are safe.',
  'crash.reload': 'Reload the page',
}
